import { AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import Welcome from '../pages/main_display';
import Login from '../components/login';
import Register from '../components/register_user';
import PrivacyPolicy from '../components/terms/privacy_policy';
import TermsOfUse from '../components/terms/terms_of_use';
import RegisterProject from '../components/project_register_index';
import ProjectIntern from "../pages/project_intern";
import EditProject from "../pages/project_edit/";
import KanbanBoard from "../components/kanban_board/";
import PrivacyPolicyPage from '../pages/terms/privacy_policy';
import TermsOfUsePage from '../pages/terms/terms_of_use';
import RegisterProjectFileUploadWebOpen from "../pages/outsider";
import OutsiderSuccess from "../pages/outsider_success";
import OutsiderCanceled from "../pages/outsider_canceled";
import RecoverPassword from "../components/recover_password";
import GuardianUser from "../components/guardian/GuardianUser/GuardianUser";
import { ViewAlerts } from "../components/guardian/ViewAlerts";
import AlertSearchByCode from "../components/guardian/ViewAlerts/AlertSearchByCode";

import { AlertDetails } from "../components/guardian/ViewAlerts/AlertDetails";
import { RegisterAlertAction } from "../components/guardian/ViewAlerts/RegisterAlertAction";
import AddUserRequest from "../components/guardian/GuardianUser/AddUserRequest/AddUserRequest";
import ViewAlertRequests from "../pages/view_alert_request";
import ListAreas from "../components/guardian/ListAreas";
import CreateArea from "../components/guardian/CreateArea";
import AreaDetails from "../components/guardian/DetailsArea"
import ArchivedAreas from "../components/guardian/ListAreas/ArchivedAreas/ArchivedAreas";
import EditAreas from "../components/guardian/EditAreas/EditAreas";
import HistoryAlert from "../components/guardian/HistoryAlert/HistoryAlert";
import AlertReportHistory from "../components/guardian/HistoryAlert/AlertReportHistory/AlertreportHistory";
import FirmsCreateArea from "../components/guardian/Firms/CreateArea";
import FirmsScanHistory from "../components/guardian/Firms/ScanHistory";
import ListAllFirmsAreas from "../components/guardian/Firms/ListAllAreas";
import FirmsAreaDetail from "../components/guardian/Firms/AreaDetail";
import VegetationSuppressionForm from "../components/guardian/VegetationSuppression/Form";
import InviteWelcomePage from "../components/guardian/InviteWelcome/InviteWelcomePage";
import GuardianRestrictedRoute from "../components/GuardianRestrictedRoute";
import MyAlertsPage from "../pages/my_alerts";
import AlertReceivedPage from "../pages/my_alerts/AlertReceivedPage";
import RespondAlertPage from "../pages/my_alerts/RespondAlertPage";
import {
  canAccessInviteWelcome,
  canCreateGuardianAlert,
  canAccessAlertRequests,
} from "../utils/userRoles";
import ChangePassword from "../components/change_password";
import OldLandingPage from "../pages/old_landing_page";
import B4CertificadoPage from "../pages/b4_and_vert_ecotech/CertificadoPage";

import MiniRemixClone from "../pages/contract_deploy_page/ContractDeployPage";
import DeleteUserRequest from "../pages/delete_user_request";
import FaleConosco from '../pages/fale_conosco';
import { userService } from "../services/userService";
import { hasUiSessionHint, persistUiSession } from "../utils/authSession";

import ControleVersoes from '../components/AppVersions/'

const PUBLIC_PATHS = new Set([
  '/',
  '/old_landing_page',
  '/certificado',
  '/login',
  '/register',
  '/privacy_policy',
  '/terms_of_use',
  '/privacy',
  '/terms_of_use_page',
  '/upload_success',
  '/upload_canceled',
  '/delete_user_request',
  '/fale-conosco',
  '/preview_b4',
]);

function isPublicPath(pathname) {
  if (PUBLIC_PATHS.has(pathname)) return true;
  if (pathname.startsWith('/recover_password')) return true;
  if (pathname.startsWith('/change_password')) return true;
  if (pathname.startsWith('/open_upload/')) return true;
  return false;
}

/** Mantém deep-link /new_alert → hub Guardian com abas + state (ex.: alertRequest). */
function RedirectNewAlertToGuardian() {
  const location = useLocation();
  return (
    <Navigate
      to="/guardian"
      replace
      state={{ ...(location.state || {}), activeTab: "new_alert" }}
    />
  );
}

/** /alerts/history-report → aba Relatórios do Guardian. */
function RedirectReportsToGuardian() {
  return <Navigate to="/guardian" replace state={{ activeTab: "reports" }} />;
}

const AppRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authChecked, setAuthChecked] = useState(() => isPublicPath(location.pathname));
  const [isAuthenticated, setIsAuthenticated] = useState(() => hasUiSessionHint());
  /** Depois da primeira sessão válida, trocar de rota não pode desmontar a árvore. */
  const sessionVerifiedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const verifySession = async () => {
      if (isPublicPath(location.pathname)) {
        if (!cancelled) setAuthChecked(true);
        return;
      }

      // Atalho de UI: se nem metadados existem, manda pro login sem round-trip.
      if (!hasUiSessionHint()) {
        sessionVerifiedRef.current = false;
        if (!cancelled) {
          setIsAuthenticated(false);
          setAuthChecked(true);
          navigate('/');
        }
        return;
      }

      // Só esconde a UI na primeira verificação. Nas próximas rotas a sessão
      // é revalidada em segundo plano — senão a aplicação inteira pisca.
      if (!sessionVerifiedRef.current) {
        setAuthChecked(false);
      }

      try {
        const me = await userService.getMe();
        if (cancelled) return;
        persistUiSession(me);
        sessionVerifiedRef.current = true;
        setIsAuthenticated(true);
      } catch {
        if (cancelled) return;
        sessionVerifiedRef.current = false;
        setIsAuthenticated(false);
        navigate('/');
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    };

    verifySession();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate]);

  if (!authChecked && !isPublicPath(location.pathname)) {
    return null;
  }

  if (
    !isPublicPath(location.pathname) &&
    authChecked &&
    !isAuthenticated
  ) {
    return (
      <AnimatePresence>
        <Routes>
          <Route exact path="*" element={<Login />} />
        </Routes>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      <Routes>
        <Route exact path="/" element={<Login />} />
        <Route exact path="/preview_b4" element={<Navigate to="/" replace />} />
        <Route exact path="/old_landing_page" element={<OldLandingPage />} />
        <Route exact path="/certificado" element={<B4CertificadoPage />} />
        {/* Login canônico na raiz; /login redireciona */}
        <Route exact path="/login" element={<Navigate to="/" replace />} />

        {/** change password*/}
        <Route exact path="/change_password" element={<ChangePassword />} />

        <Route exact path="/register" element={<Register />} />
        <Route exact path="/privacy_policy" element={<PrivacyPolicy />} />
        <Route exact path="/terms_of_use" element={<TermsOfUse />} />
        <Route exact path="/project_register" element={<RegisterProject />} />
        <Route exact path="/welcome" element={<Welcome />} />

        {/* Painéis com URL própria (Fase 2 — reaproveitam o shell Welcome) */}
        <Route exact path="/dashboard" element={<Welcome panel="Dashboard" />} />
        <Route exact path="/my-alerts" element={<MyAlertsPage />} />
        <Route exact path="/my-alerts/:alertId" element={<AlertReceivedPage />} />
        <Route exact path="/my-alerts/:alertId/respond" element={<RespondAlertPage />} />
        <Route exact path="/users" element={<Welcome panel="Usuários" />} />
        <Route exact path="/projects" element={<Welcome panel="Projetos" />} />
        <Route exact path="/accounts" element={<Welcome panel="Contas" />} />
        <Route exact path="/profile" element={<Welcome panel="Meu Perfil" />} />
        <Route exact path="/settings" element={<Welcome panel="Configurações" />} />

        <Route exact path="/intern_project/:projectId" element={<ProjectIntern />} />
        <Route exact path="/intern_project" element={<ProjectIntern />} />
        <Route exact path="/edit_intern_project" element={<EditProject />} />
        <Route exact path="/analysis_and_development" element={<KanbanBoard />} />
        <Route exact path="/privacy" element={<PrivacyPolicyPage />} />
        <Route exact path="/terms_of_use_page" element={<TermsOfUsePage />} />
        <Route exact path="/open_upload/:access" element={<RegisterProjectFileUploadWebOpen />} />
        <Route exact path="/upload_success" element={<OutsiderSuccess />} />
        <Route exact path="/upload_canceled" element={<OutsiderCanceled />} />
        <Route exact path="/recover_password/:code/:hash" element={<RecoverPassword />} />
        <Route exact path="/user_request" element={<GuardianUser />} />
        <Route
          exact
          path="/new_alert"
          element={
            <GuardianRestrictedRoute allowed={canCreateGuardianAlert}>
              <RedirectNewAlertToGuardian />
            </GuardianRestrictedRoute>
          }
        />

        <Route exact path="/view_alerts" element={<ViewAlerts />} />
        <Route exact path="/alerts/buscar/:cod_alerta" element={<AlertSearchByCode />} />
        <Route exact path="/add_user_request" element={<AddUserRequest />} />
        <Route exact path="/alert-details" element={<AlertDetails />} />
        <Route exact path="/alert-register-action" element={<RegisterAlertAction />} />
        {/* Hub Guardian com shell (sidebar + Voltar consistentes) */}
        <Route exact path="/guardian" element={<Welcome panel="Guardião" />} />
        <Route exact path="/create_area" element={<CreateArea />} />
        <Route exact path="/areas" element={<ListAreas />} />
        <Route exact path="/area_details/:id_code" element={<AreaDetails />} />
        <Route exact path="/archived_areas" element={<ArchivedAreas />} />
        <Route exact path="/edit_area/:id_code" element={<EditAreas />} />
        <Route exact path="/alerts/history/:id_code" element={<HistoryAlert />} />
        <Route exact path="/alerts/history-report" element={<RedirectReportsToGuardian />} />
        <Route exact path="/alerts/history-report/:area_id" element={<AlertReportHistory />} />
        <Route exact path="/firms/areas" element={<ListAllFirmsAreas />} />
        <Route exact path="/firms/create" element={<FirmsCreateArea />} />
        <Route exact path="/firms/areas/:fireAreaId/history" element={<FirmsScanHistory />} />
        <Route exact path="/firms/areas/:fireAreaId" element={<FirmsAreaDetail />} />
        <Route exact path="/area_details/:id_code/firms/create" element={<FirmsCreateArea />} />
        <Route exact path="/area_details/:id_code/firms/:fireAreaId/history" element={<FirmsScanHistory />} />
        <Route
          exact
          path="/area_details/:id_code/vegetation-suppression/new"
          element={<VegetationSuppressionForm />}
        />
        <Route
          exact
          path="/area_details/:id_code/vegetation-suppression/:recordId"
          element={<VegetationSuppressionForm />}
        />

         {/*Rota de versionamento do app*/}
        <Route exact path="/app_versions" element={<ControleVersoes/>} />


        <Route
          exact
          path="/view_alert_request"
          element={
            <GuardianRestrictedRoute allowed={canAccessAlertRequests}>
              <ViewAlertRequests />
            </GuardianRestrictedRoute>
          }
        />
        <Route
          exact
          path="/guardian/invite-welcome"
          element={
            <GuardianRestrictedRoute allowed={canAccessInviteWelcome}>
              <InviteWelcomePage />
            </GuardianRestrictedRoute>
          }
        />

        <Route exact path="/delete_user_request" element={<DeleteUserRequest />} />
        <Route exact path="/fale-conosco" element={<FaleConosco />} />


        <Route exact path="/deploy_contract" element={<MiniRemixClone />} />

        {/*<Route exact path="/edit_user" element={<UserIntern/>}/>*/}
      </Routes>
    </AnimatePresence>
  );
};

export default AppRoutes;
